import React, { useState, useMemo, useEffect } from 'react';
import { Formik, ErrorMessage } from 'formik';
import isArray from 'lodash/isArray';
import clone from 'lodash/clone';

import LoadMask from 'components/LoadMask';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicTextArea from 'components/BasicTextArea';
import BasicLabel from 'components/BasicLabel';
import DateSelect from 'components/DateSelect';
import HorizontalLoader from 'components/HorizontalLoader';

import { ProfileEditEducationFormValidationSchema } from 'scenes/Form/components/ProfileEditEducationForm/validate';
import { ENDPOINT } from 'constants/endpoints';
import { useApi } from 'hooks/useApi';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

import './styles.css';

const ProfileEditEducationForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params,
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    init: false,
    fields: {},
  });

  const [endpointGet, endpointSave] = useMemo(() => {
    const selfEdit = !params || !params.externalEdit;
    return selfEdit
      ? [ENDPOINT.FORM.GET.POSITION, ENDPOINT.FORM.SAVE.POSITION]
      : [
          ENDPOINT.FORM.GET.EDIT_CANDIDATE_PROFILE_POSITION,
          ENDPOINT.FORM.SAVE.EDIT_CANDIDATE_PROFILE_POSITION,
        ];
  }, []);

  useEffect(() => {
    api.get(
      endpointGet,
      { id: params.id, cId: params.cId },
      {
        handleError: handleError,
        handleSuccess: data => {
          const endDate = {
            month: data.fields.EndMonth,
            year: data.fields.EndYear,
          };

          const startDate =
            data.fields.StartMonth && data.fields.StartYear
              ? {
                  month: data.fields.StartMonth,
                  year: data.fields.StartYear,
                }
              : null;

          setState({
            ...state,
            init: true,
            fields: {
              endDate,
              startDate,
              Description: data.fields.Description || '',
              Present: !!data.fields.Present,
            },
            options: {
              Date: {
                ...data.options.Date,
                year: Object.values(data.options.Date.year).sort(
                  (a, b) => Number(b.value) - Number(a.value)
                ),
              },
            },
          });
        },
      }
    );
  }, []);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const preparedValues = { ...clone(values), id: params.id };

    if (params.cId) {
      preparedValues.cId = params.cId;
    }

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null) {
        delete preparedValues[field];
      } else if (field === 'startDate') {
        preparedValues.StartMonth = data.month;
        preparedValues.StartYear = data.year;
        delete preparedValues.startDate;
      } else if (field === 'endDate') {
        preparedValues.EndMonth = data.month;
        preparedValues.EndYear = data.year;
        delete preparedValues.endDate;
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.id);
      } else if (typeof data === 'object') {
        preparedValues[field] = data.value;
      }
    });

    api.post(endpointSave, preparedValues, {
      handleForbidden: handleError,
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: () => proceed(values),
    });
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          validationSchema={ProfileEditEducationFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}
              <div className="form__group form__group--compact">
                <BasicLabel
                  name="Description"
                  required={false}
                  text="Description"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="Description"
                  content={values.Description}
                  resize={false}
                  placeholder="0 characters of the 2000 character limit"
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.Description && !!touched.Description) ||
                    !touched.Description
                  }
                />

                <ErrorMessage
                  name="Description"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              {values.startDate && (
                <div className="form__group form__group--compact">
                  <DateSelect
                    required
                    label="Start Date"
                    name="startDate"
                    controlFunc={setFieldValue}
                    touchFunc={setFieldTouched}
                    values={values.startDate}
                    yearOptions={state.options.Date.year}
                    isValid={
                      (!errors.startDate && !!touched.startDate) ||
                      !touched.startDate
                    }
                  />

                  <ErrorMessage
                    name="startDate"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              {!values.Present && (
                <div className="form__group form__group--compact">
                  <DateSelect
                    required
                    label="End Date"
                    name="endDate"
                    controlFunc={setFieldValue}
                    touchFunc={setFieldTouched}
                    values={values.endDate}
                    yearOptions={state.options.Date.year}
                    isLast={true}
                    isValid={
                      (!errors.endDate && !!touched.endDate) || !touched.endDate
                    }
                  />

                  <ErrorMessage
                    name="endDate"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default ProfileEditEducationForm;
